/**
 * FlagFormDialog — create/edit a feature flag (STORY-008-009). A `BottomSheet`
 * form: key (create-only, immutable on edit), enabled switch, description, and
 * a targeting-rule BUILDER. Each rule row = an `attribute` + comma-separated
 * `values`; on submit we serialise to the SHIPPED `FlagRule` shape
 * ({ attribute, values: string[] }, both non-empty — backend `flags_schemas.py`
 * rejects empties with 422, so we drop incomplete rows client-side). Edit mode
 * also exposes Delete. Create surfaces the 409 `feature_flag.already_exists`.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet, Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import {
  useCreateFlag,
  useDeleteFlag,
  useUpdateFlag,
} from "@/features/admin/feature-flags/hooks";
import type {
  AdminFeatureFlag,
  FlagRule,
} from "@/features/admin/feature-flags/api";

export interface FlagFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initial: AdminFeatureFlag | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface RuleRow {
  attribute: string;
  valuesText: string;
}

const KEY_RE = /^[a-z0-9._-]{1,128}$/;

function rowsFromRules(rules: FlagRule[]): RuleRow[] {
  return rules.map((r) => ({
    attribute: r.attribute,
    valuesText: r.values.join(", "),
  }));
}

function rulesFromRows(rows: RuleRow[]): FlagRule[] {
  const out: FlagRule[] = [];
  for (const row of rows) {
    const attribute = row.attribute.trim();
    const values = row.valuesText
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (attribute.length === 0 || values.length === 0) continue;
    out.push({ attribute, values });
  }
  return out;
}

export function FlagFormDialog({
  open,
  mode,
  initial,
  onClose,
  onSuccess,
}: FlagFormDialogProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const create = useCreateFlag();
  const update = useUpdateFlag();
  const remove = useDeleteFlag();

  const [keyVal, setKeyVal] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setConfirmDelete(false);
    if (mode === "edit" && initial) {
      setKeyVal(initial.key);
      setEnabled(initial.enabled);
      setDescription(initial.description ?? "");
      setRows(rowsFromRules(initial.rules));
    } else {
      setKeyVal("");
      setEnabled(false);
      setDescription("");
      setRows([]);
    }
  }, [open, mode, initial]);

  const keyValid = useMemo(
    () => mode === "edit" || KEY_RE.test(keyVal.trim()),
    [mode, keyVal],
  );
  const isPending = create.isPending || update.isPending || remove.isPending;

  const addRow = (): void =>
    setRows((r) => [...r, { attribute: "", valuesText: "" }]);
  const patchRow = (i: number, patch: Partial<RuleRow>): void =>
    setRows((r) => r.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const removeRow = (i: number): void =>
    setRows((r) => r.filter((_, j) => j !== i));

  const handleSubmit = async (): Promise<void> => {
    if (!keyValid) {
      setError(t("featureFlags.form.keyInvalid"));
      return;
    }
    setError(null);
    const rules = rulesFromRows(rows);
    const desc = description.trim() === "" ? null : description.trim();
    try {
      if (mode === "create") {
        await create.mutateAsync({
          key: keyVal.trim(),
          enabled,
          description: desc,
          rules,
        });
      } else {
        await update.mutateAsync({
          key: keyVal,
          body: { enabled, description: desc, rules },
        });
      }
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.code === "feature_flag.already_exists") {
        setError(t("featureFlags.form.conflict"));
      } else {
        setError(t("error.generic"));
      }
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!initial) return;
    try {
      await remove.mutateAsync(initial.key);
      onSuccess();
    } catch {
      setError(t("error.generic"));
    }
  };

  const title =
    mode === "create"
      ? t("featureFlags.form.createTitle")
      : t("featureFlags.form.editTitle");

  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      <div className="flex flex-col gap-4" data-testid="flag-form">
        {/* Key */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="flag-key" className="text-sm font-medium">
            {t("featureFlags.form.keyLabel")}
          </label>
          <input
            id="flag-key"
            data-testid="flag-key-input"
            value={keyVal}
            disabled={mode === "edit"}
            onChange={(e) => setKeyVal(e.target.value)}
            placeholder={t("featureFlags.form.keyPlaceholder")}
            className="min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)] disabled:opacity-60"
          />
          <p className="text-xs text-[var(--g-secondary)]">
            {t("featureFlags.form.keyHint")}
          </p>
        </div>

        {/* Enabled */}
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            data-testid="flag-enabled-input"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-5 w-5 accent-[var(--g-primary)]"
          />
          {t("featureFlags.form.enabledLabel")}
        </label>

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="flag-desc" className="text-sm font-medium">
            {t("featureFlags.form.descriptionLabel")}
          </label>
          <textarea
            id="flag-desc"
            data-testid="flag-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={1024}
            className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
          />
        </div>

        {/* Targeting-rule builder */}
        <fieldset className="flex flex-col gap-2" data-testid="flag-rules">
          <legend className="text-sm font-medium">
            {t("featureFlags.form.rulesLegend")}
          </legend>
          <p className="text-xs text-[var(--g-secondary)]">
            {t("featureFlags.form.rulesHint")}
          </p>
          {rows.length === 0 ? (
            <p
              data-testid="flag-rules-empty"
              className="text-xs text-[var(--g-secondary)]"
            >
              {t("featureFlags.form.rulesEmpty")}
            </p>
          ) : null}
          {rows.map((row, i) => (
            <div
              key={i}
              data-testid={`flag-rule-row-${i}`}
              className="flex flex-col gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-2 sm:flex-row sm:items-center"
            >
              <input
                data-testid={`flag-rule-attribute-${i}`}
                value={row.attribute}
                onChange={(e) => patchRow(i, { attribute: e.target.value })}
                placeholder={t("featureFlags.form.attributePlaceholder")}
                aria-label={t("featureFlags.form.attributeLabel")}
                maxLength={64}
                className="min-h-[44px] flex-1 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
              />
              <input
                data-testid={`flag-rule-values-${i}`}
                value={row.valuesText}
                onChange={(e) => patchRow(i, { valuesText: e.target.value })}
                placeholder={t("featureFlags.form.valuesPlaceholder")}
                aria-label={t("featureFlags.form.valuesLabel")}
                className="min-h-[44px] flex-1 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
              />
              <Button
                variant="ghost"
                size="sm"
                data-testid={`flag-rule-remove-${i}`}
                onClick={() => removeRow(i)}
                aria-label={t("featureFlags.form.removeRule")}
              >
                {t("common.remove")}
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            data-testid="flag-rule-add"
            onClick={addRow}
          >
            {t("featureFlags.form.addRule")}
          </Button>
        </fieldset>

        {error ? (
          <p role="alert" data-testid="flag-form-error" className="text-sm text-[var(--g-error)]">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-2">
          {mode === "edit" ? (
            confirmDelete ? (
              <Button
                variant="destructive"
                size="sm"
                data-testid="flag-delete-confirm"
                loading={remove.isPending}
                onClick={() => void handleDelete()}
              >
                {t("featureFlags.form.deleteConfirm")}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                data-testid="flag-delete"
                onClick={() => setConfirmDelete(true)}
              >
                {t("featureFlags.form.delete")}
              </Button>
            )
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="md" onClick={onClose} disabled={isPending}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="md"
              data-testid="flag-form-submit"
              loading={create.isPending || update.isPending}
              disabled={!keyValid || isPending}
              onClick={() => void handleSubmit()}
            >
              {t("common.save")}
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}

export default FlagFormDialog;
